import {
  Alert,
  AlertIcon,
  Box,
  Button,
  PinInput,
  PinInputField,
  Stack,
  Text,
} from '@chakra-ui/react'
import { useCallback, useMemo, useState } from 'react'
import PhoneInput from 'react-phone-input-2'
import 'react-phone-input-2/lib/style.css'

import logger from '@/utils/logger'

import UserQ from '@/queries/UserQ'
import { useQuery } from '@/client/context/query'
import { useUser } from '@/client/context/user'
import useAPIWrapper from '@/client/hooks/useAPIWrapper'
import RPC, { RPCRefresh } from '@/client/rpc'
import { faq, getSettingsUrl } from '@/client/services/url'
import { getArticleUrl } from '@/client/services/url/faq'
import {
  ERROR_USER_PHONE_NUMBER_IN_USE,
  USER_PHONE_VERIFICATION_CODE_LENGTH,
} from '@/client/services/user'

import ShwLink from '@/components/ShwLink'
import { useToaster } from '@/components/Toast/useToaster'
import ModalV3 from '@/components/UI/Modal/ModalV3'

export default function useEditPhoneNumberModal({
  type,
  optional = false,
  defaultIsOpened = false,
}: {
  type: 'add' | 'edit'
  optional?: boolean
  defaultIsOpened?: boolean
}): [JSX.Element, ({ onClose }: { onClose?: () => void }) => void] {
  const toast = useToaster()

  const user = useUser()
  const query = useQuery()

  const me = query.one(UserQ.where({ id: user.id }))
  const oldNumber = me(UserQ.phone_number)

  const [showModal, setShowModal] = useState(defaultIsOpened)
  const [phoneNumber, setPhoneNumber] = useState('')
  const [token, setToken] = useState('')
  const [phoneNumberVerificationError, setPhoneNumberVerificationError] = useState<string | null>(
    null
  )
  const [phoneNumberInputError, setPhoneNumberInputError] = useState<string | null>(null)
  const [shouldDisableVerification, setShouldDisableVerification] = useState(false)
  const [submittingForm, setSubmittingForm] = useState(false)

  const [editPhoneStep, setEditPhoneStep] = useState<'add' | 'verify'>('add')

  const [onClose, setOnClose] = useState<(() => void) | null>(null)

  const handleReset = useCallback(() => {
    setPhoneNumber('')
    setToken('')
    setPhoneNumberInputError(null)
    setPhoneNumberVerificationError(null)
    setEditPhoneStep('add')
  }, [])

  const handleClose = useCallback(() => {
    handleReset()
    setShowModal(false)
    if (onClose != null) {
      onClose()
    }
  }, [handleReset, onClose])

  const [handleInputPhoneNumber] = useAPIWrapper(
    async () => {
      try {
        setSubmittingForm(true)
        if (`+${phoneNumber}` === oldNumber) {
          setPhoneNumberInputError('You cannot change to the same phone number as before.')
        } else {
          await RPC.requestChangePhoneVerificationToken({ phoneNumber: `+${phoneNumber}` })
          setEditPhoneStep('verify')
        }
      } catch (err) {
        setPhoneNumberInputError(
          err.message ??
            `There was an error submitting your phone number. Please try again or reach out to support at ${faq.getUrl()}.`
        )
        logger.error('Error requesting phone verification code', { err })
      } finally {
        setSubmittingForm(false)
      }
    },
    [phoneNumber, oldNumber],
    'Request Phone Number Verification'
  )

  const [handleVerifyPhoneNumber] = useAPIWrapper(
    async () => {
      try {
        setSubmittingForm(true)
        const params = {
          phoneNumber: `+${phoneNumber}`,
          token,
        }
        await RPCRefresh.verifyChangePhoneVerificationToken(params)
        toast({
          title: 'Successfully saved phone number!',
          status: 'success',
        })
        handleClose()
      } catch (err) {
        setPhoneNumberVerificationError(err?.message ?? err)

        const message: string | undefined = err?.message
        if (message?.includes(ERROR_USER_PHONE_NUMBER_IN_USE) ?? false) {
          // Twilio has already verified the code and will error if we try to verify again
          setShouldDisableVerification(true)
        }
        logger.error('Error requesting phone verification code', err)
      } finally {
        setSubmittingForm(false)
      }
    },
    [phoneNumber, token, toast, handleClose],
    'Verify Phone Number'
  )

  const preferredCountries = useMemo(() => ['us'], [])

  const title =
    type === 'add' ? `Add a Phone Number${optional ? ' (Optional)' : ''}` : 'Edit Phone Number'

  const content = (
    <ModalV3
      size="lg"
      open={showModal}
      onClose={handleClose}
      title={title}
      body={() =>
        editPhoneStep === 'add' ? (
          <Stack direction="column" spacing={3}>
            <Text mb={1}>
              Enter your phone number to get Schoolhouse updates and text reminders before your
              sessions begin. Standard text message &amp; data rates may apply.
            </Text>
            {phoneNumberInputError == null ? null : (
              <Alert status="error" borderRadius="8px" mb={6}>
                <AlertIcon />
                {phoneNumberInputError}
              </Alert>
            )}
            <Box>
              <PhoneInput
                value={phoneNumber}
                onChange={setPhoneNumber}
                preferredCountries={preferredCountries}
                country="us"
              />
            </Box>
            <Text mb={1}>
              You can select what notifications you want to receive at this phone number through the{' '}
              <ShwLink isExternal href={getSettingsUrl('notifications')}>
                Notification Settings
              </ShwLink>
              .
            </Text>
            <Text>
              Note: Users outside of the United States may receive limited text message
              notifications at this time.
            </Text>
          </Stack>
        ) : (
          <Stack direction="column" spacing={3}>
            <Text>Enter the 6-digit code sent to you at +{phoneNumber}.</Text>
            {phoneNumberVerificationError == null ? null : (
              <Alert status="error" borderRadius="8px" mb={6}>
                <AlertIcon />
                <Text fontWeight="bold">{phoneNumberVerificationError}</Text>
              </Alert>
            )}
            <Box>
              <Stack direction="row" justifyContent="center" mt="16px">
                <PinInput otp value={token} onChange={setToken} size="lg">
                  <PinInputField />
                  <PinInputField />
                  <PinInputField />
                  <PinInputField />
                  <PinInputField />
                  <PinInputField />
                </PinInput>
              </Stack>
            </Box>
            <Text fontSize="sm" color="fg.muted" textAlign="center">
              Not receiving the code? Reach out to{' '}
              <ShwLink isExternal href={getArticleUrl('how-do-i-contact-the-schoolhouse-team')}>
                User Support
              </ShwLink>{' '}
              for help.
            </Text>
          </Stack>
        )
      }
      leftFooterContent={
        editPhoneStep === 'verify'
          ? () => (
              <Button variant="outline" onClick={handleReset}>
                Back
              </Button>
            )
          : undefined
      }
      rightFooterContent={() => (
        <Button
          onClick={editPhoneStep === 'add' ? handleInputPhoneNumber : handleVerifyPhoneNumber}
          isDisabled={
            submittingForm ||
            (editPhoneStep === 'add'
              ? phoneNumber.length === 0
              : shouldDisableVerification || token.length !== USER_PHONE_VERIFICATION_CODE_LENGTH)
          }
        >
          {editPhoneStep === 'add' ? 'Next' : 'Verify Code'}
        </Button>
      )}
    />
  )

  const enableCallback = useCallback(({ onClose: onModalClose }: { onClose?: () => void }) => {
    setShowModal(true)
    if (onModalClose != null) {
      setOnClose(() => onModalClose)
    }
  }, [])

  return [content, enableCallback]
}
